/**
 * Organization Service
 *
 * This service is responsible for managing organization data.
 * It provides methods to get, create, update, and delete organization data.
 * It also provides methods to get organization user data and set user current organization.
 *
 * @author Rammetec
 * @version 1.0.0
 * @since 2025-11-15
 */
import { rtLogService } from '@rammetec/core';
import { repositoryFactory, repositoryResolver, DataSourceStrategy, CacheStrategy } from '@rammetec/data-access';
import { ORGANIZATION_DATA, USER_ORGANIZATION } from '../types/organization.js';
import { USER_DATA, ORGANIZATION_USER_ORG_DATA } from '../types/user.js';
import { userService } from './userService.js';
import { profileService } from './profileService.js';
import { moduleService, normalizeModuleIdList } from './moduleService.js';
import { moduleGroupService } from './moduleGroupService.js';
import { organizationModuleService } from './organizationModuleService.js';
import { planService } from './planService.js';
import { lifecycleService } from './lifecycleService.js';
import { auditService } from './auditService.js';
import { normalizeCnpj } from '../party/cnpj.js';
import { normalizeTag } from '../utils/moduleTags.js';
import { entitledModuleIds } from '../utils/moduleResolution.js';
class OrganizationService {
    async getOrganizationRepository() {
        return await repositoryFactory.getRepository('org', ORGANIZATION_DATA, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
    }
    /**
     * Returns the list of valid organizations for a user.
     * Filters organizations by checking if the user's inactivationDate in each organization is valid.
     * @param userId - The user ID
     * @returns Array of valid OrganizationData
     */
    async getValidOrganizations(userId) {
        try {
            // Get all organization IDs for the user
            const orgIds = await userService.getUserOrganizationIds(userId);
            if (!orgIds || orgIds.length === 0) {
                return [];
            }
            // Validate access for each organization and filter valid ones
            const validOrganizations = [];
            for (const orgId of orgIds) {
                try {
                    // Check if user has valid access to this organization
                    const orgUserRepo = await repositoryFactory.getRepository(`org/${orgId}/users`, ORGANIZATION_USER_ORG_DATA, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
                    let orgUserData = null;
                    try {
                        orgUserData = await orgUserRepo.getById(userId);
                    }
                    catch {
                        // Org user data might not exist, skip this organization
                        continue;
                    }
                    if (!orgUserData) {
                        continue;
                    }
                    // Active = no inactivation cutoff, or cutoff still in the future.
                    const hasValidAccess = lifecycleService.isActive(orgUserData);
                    if (hasValidAccess) {
                        // Get organization data
                        const orgData = await this.getOrganizationRepository().then(repo => repo.getById(orgId));
                        if (orgData) {
                            validOrganizations.push(orgData);
                        }
                    }
                }
                catch (error) {
                    // Skip organizations that fail validation or don't exist
                    rtLogService.warn('Skipping invalid organization', { userId, orgId, error });
                }
            }
            return validOrganizations;
        }
        catch (error) {
            rtLogService.error('Error fetching valid organizations', { userId, error });
            throw error;
        }
    }
    /**
     * Sets the current organization for a user in the database.
     * Validates that the organization exists in the user's valid organizations list.
     * @param userId - The user ID
     * @param orgId - The organization ID to set as current
     * @throws Error if the organization is not valid for the user
     */
    async setCurrentOrganization(userId, orgId) {
        try {
            // Get valid organizations for the user
            const validOrganizations = await this.getValidOrganizations(userId);
            // Check if the requested organization is in the valid list
            const isValidOrg = validOrganizations.some(org => org.id === orgId);
            if (!isValidOrg) {
                throw new Error(`Organization ${orgId} is not valid for user ${userId} or user does not have access`);
            }
            // Set the current organization
            await userService.setUserCurrentOrganization(userId, orgId);
            rtLogService.info('Current organization set', { userId, orgId });
        }
        catch (error) {
            rtLogService.error('Error setting current organization', { userId, orgId, error });
            throw error;
        }
    }
    /**
     * Returns the organization user data (user data and org user data separately).
     * @param orgId - The organization ID
     * @param userId - The user ID
     * @returns Object with userData and orgUserData, or null if user not found
     */
    async getOrganizationUser(orgId, userId) {
        try {
            const userRepo = await repositoryFactory.getRepository('user', USER_DATA, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
            const orgUserRepo = await repositoryFactory.getRepository(`org/${orgId}/users`, ORGANIZATION_USER_ORG_DATA, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
            const userData = await userRepo.getById(userId);
            if (!userData) {
                return null;
            }
            let orgUserData = null;
            try {
                orgUserData = await orgUserRepo.getById(userId);
            }
            catch {
                // Org user data might not exist yet, that's okay
            }
            return { userData, orgUserData };
        }
        catch (error) {
            rtLogService.error('Error fetching organization user', { orgId, userId, error });
            return null;
        }
    }
    /**
     * Returns the organization data.
     * @param orgId - The organization ID
     * @returns OrganizationData or null if not found
     */
    async getOrganizationData(orgId) {
        try {
            const orgData = await this.getOrganizationRepository().then(repo => repo.getById(orgId));
            return orgData;
        }
        catch (error) {
            rtLogService.error('Error fetching organization data', { orgId, error });
            return null;
        }
    }
    /**
     * Returns all organizations (superAdmin view). Apps should sort/filter client-side.
     */
    async getAllOrganizations() {
        try {
            const repo = await this.getOrganizationRepository();
            return await repo.getAll();
        }
        catch (error) {
            rtLogService.error('Error fetching all organizations', { error });
            throw error;
        }
    }
    /**
     * Updates an organization document. Pass only the fields you want to change.
     */
    async updateOrganizationData(orgId, data) {
        try {
            const repo = await this.getOrganizationRepository();
            // Keep the denormalized `active` flag in sync whenever the cutoff changes.
            let withActive = 'inactivationDate' in data
                ? { ...data, active: lifecycleService.isActive({ inactivationDate: data.inactivationDate }) }
                : { ...data };
            if ('cnpj' in withActive) {
                const raw = withActive.cnpj;
                withActive = {
                    ...withActive,
                    cnpj: raw == null || raw === '' ? null : normalizeCnpj(String(raw)) || null,
                };
            }
            // Refresh last-modified metadata on every update.
            const patch = auditService.stampOnUpdate(withActive);
            await repo.update(orgId, patch);
            rtLogService.info('Organization updated', { orgId });
        }
        catch (error) {
            rtLogService.error('Error updating organization', { orgId, error });
            throw error;
        }
    }
    /**
     * Writes the VectorHub product config to products.vectorhub on the org doc.
     *
     * Firestore `updateDoc` replaces nested maps wholesale — `{ products: { vectorhub } }`
     * would clobber sibling keys (e.g. `products.workshop`). Read-modify-write the whole
     * `products` map so only the vectorhub key changes.
     * `contractedAt` is set only on the first contraction; subsequent edits preserve it.
     */
    async setVectorhubProduct(orgId, config, updatedBy, existingContractedAt) {
        const now = new Date().toISOString();
        const vhConfig = { ...config, updatedAt: now, updatedBy };
        // Only assign contractedAt when it has a real value — Firestore rejects nested undefined.
        const contractedAt = config.contracted && !existingContractedAt ? now : existingContractedAt;
        if (contractedAt)
            vhConfig.contractedAt = contractedAt;
        const products = await this.mergeProductIntoOrg(orgId, { vectorhub: vhConfig });
        await this.updateOrganizationData(orgId, { products });
        rtLogService.info('VectorHub product config updated', { orgId, contracted: config.contracted });
    }
    /**
     * Writes the Workshop product config to products.workshop on the org doc.
     *
     * Same read-modify-write as setVectorhubProduct — see that method for why.
     * `contractedAt` is set only on the first contraction; subsequent edits preserve it.
     */
    async setWorkshopProduct(orgId, config, updatedBy, existingContractedAt) {
        const now = new Date().toISOString();
        const woConfig = { ...config, updatedAt: now, updatedBy };
        const contractedAt = config.contracted && !existingContractedAt ? now : existingContractedAt;
        if (contractedAt)
            woConfig.contractedAt = contractedAt;
        const products = await this.mergeProductIntoOrg(orgId, { workshop: woConfig });
        await this.updateOrganizationData(orgId, { products });
        rtLogService.info('Workshop product config updated', { orgId, contracted: config.contracted });
    }
    /**
     * Writes the AI product config to products.ai on the org doc.
     *
     * Same read-modify-write as setWorkshopProduct — see setVectorhubProduct for why.
     * `contractedAt` is set only on the first contraction; subsequent edits preserve it.
     * `monthlyTokens` is the absolute monthly allowance; `purchasedTokens` is the prepaid
     * balance (callers that sell packs must add to the existing balance, not replace it).
     */
    async setAiProduct(orgId, config, updatedBy, existingContractedAt) {
        const now = new Date().toISOString();
        const aiConfig = { ...config, updatedAt: now, updatedBy };
        const contractedAt = config.contracted && !existingContractedAt ? now : existingContractedAt;
        if (contractedAt)
            aiConfig.contractedAt = contractedAt;
        const products = await this.mergeProductIntoOrg(orgId, { ai: aiConfig });
        await this.updateOrganizationData(orgId, { products });
        rtLogService.info('AI product config updated', { orgId, contracted: config.contracted });
    }
    /**
     * Reads the org's current `products` map and merges the given product key(s).
     * Required because Firestore update replaces nested maps entirely (no deep merge).
     */
    async mergeProductIntoOrg(orgId, patch) {
        const org = await this.getOrganizationData(orgId);
        return { ...(org?.products ?? {}), ...patch };
    }
    /**
     * Deletes an organization document. Does NOT clean up cross-collection user
     * links — that orchestration (System API / application layer) belongs to the
     * caller, which then invokes this for the data-side delete.
     */
    async deleteOrganization(orgId) {
        try {
            const repo = await this.getOrganizationRepository();
            await repo.delete(orgId);
            rtLogService.info('Organization deleted', { orgId });
        }
        catch (error) {
            rtLogService.error('Error deleting organization', { orgId, error });
            throw error;
        }
    }
    /**
     * Returns all users in an organization with their data (userData and orgUserData separately).
     * @param orgId - The organization ID
     * @returns Array of objects with userData and orgUserData
     */
    async getOrganizationUsers(orgId) {
        try {
            const organizationUserOrgRepository = await repositoryFactory.getRepository(`org/${orgId}/users`, ORGANIZATION_USER_ORG_DATA, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
            // Fetch organization user data (only role, inactivationDate, inclusionDate)
            const orgUsersList = await organizationUserOrgRepository.getAll({});
            // For each org user, fetch the corresponding user data
            const users = [];
            for (const orgUser of orgUsersList) {
                try {
                    const userData = await userService.getUserData(orgUser.id);
                    if (userData) {
                        users.push({ userData, orgUserData: orgUser });
                    }
                }
                catch {
                    // User not found in user collection, skip or log
                    rtLogService.warn("User data not found for organization user", {
                        userId: orgUser.id,
                        organizationId: orgId,
                    });
                }
            }
            // Sort by name
            users.sort((a, b) => (a.userData.name || '').localeCompare(b.userData.name || ''));
            return users;
        }
        catch (error) {
            rtLogService.error("Error fetching organization users", { error, organizationId: orgId });
            throw error;
        }
    }
    /**
     * Checks if there is at least one organization with valid access for the user.
     * @param userId - The user ID
     * @returns true if there's at least one valid organization, false otherwise
     */
    async hasValidOrganizationAvailable(userId) {
        try {
            const validOrganizations = await this.getValidOrganizations(userId);
            return validOrganizations.length > 0;
        }
        catch (error) {
            rtLogService.error('Error checking for valid organizations', { userId, error });
            return false;
        }
    }
    /**
     * Creates a new organization with full initial setup:
     * 1. Creates the organization document in org/<orgId>
     * 2. Adds the current user as system admin in org/<orgId>/users/<userId> and user/<userId>/orgs/<orgId>
     * 3. Seeds modules via GlobalConfig.defaultPlanIds → planIds + syncPlans (0152)
     * 4. Creates an "ADM" (Administrador) profile with group grants (+ loose module grants)
     * 5. Assigns the admin user to the "ADM" profile
     *
     * @param orgData - Organization data (name, abbreviation, etc.)
     * @param adminUserId - The user ID of the admin creating the organization
     * @param onAddUserToOrg - Optional callback to handle the user-side link (user/<userId>/orgs/<orgId>)
     *   via an external service (e.g. System API). When provided, the direct Firestore write is skipped.
     *   Signature: (orgId, userId, role) => Promise<void>
     * @returns The created organization data with generated ID
     */
    async createOrganization(orgData, adminUserId, onAddUserToOrg) {
        try {
            // 1. Create the organization document
            const orgRepo = await this.getOrganizationRepository();
            const newOrg = await orgRepo.create({
                name: orgData.name,
                abbreviation: orgData.abbreviation,
                // Firestore rejects undefined on create — omit optional FK when unset.
                ...(orgData.holding ? { holding: orgData.holding } : {}),
                active: true,
                ...auditService.stampOnCreate({}),
            });
            const orgId = newOrg.id;
            rtLogService.info('Organization created', { orgId, name: orgData.name });
            // 2. Add admin user to the organization (both sides of the relationship)
            // 2a. Add in org/<orgId>/users/<userId>
            const orgUserRepo = await repositoryFactory.getRepository(`org/${orgId}/users`, ORGANIZATION_USER_ORG_DATA, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
            await orgUserRepo.create({
                id: adminUserId,
                role: 'system',
                inclusionDate: new Date(),
                allowOwnProfile: false,
            });
            // 2b. Add in user/<userId>/orgs/<orgId> - via callback (System API) or direct Firestore write
            if (onAddUserToOrg) {
                await onAddUserToOrg(orgId, adminUserId, 'system');
            }
            else {
                const userOrgRepo = await repositoryFactory.getRepository(`user/${adminUserId}/orgs`, USER_ORGANIZATION, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
                await userOrgRepo.create({
                    id: orgId,
                    orgId: orgId,
                });
            }
            rtLogService.info('Admin user added to organization', { orgId, adminUserId });
            // 3. Seed modules from GlobalConfig.defaultPlanIds (0152)
            try {
                const globalConfigRepo = await repositoryResolver.resolve('GlobalConfig');
                const globalConfig = await globalConfigRepo.getById('default');
                const defaultPlanIds = normalizeModuleIdList(globalConfig?.defaultPlanIds);
                if (defaultPlanIds.length > 0) {
                    await this.seedOrgFromDefaultPlans(orgId, orgRepo, defaultPlanIds);
                }
                else {
                    rtLogService.warn('No defaultPlanIds in GlobalConfig — new organization seeded without modules', { orgId });
                }
            }
            catch (error) {
                rtLogService.warn('Failed to seed initial modules for organization, continuing without them', {
                    orgId,
                    error,
                });
            }
            // 4. Create "ADM" profile — group grants for inheritance + module grants for loose leaves (0152 §3.3)
            const [orgModules, groups] = await Promise.all([
                organizationModuleService.getOrgModules(orgId),
                moduleGroupService.getGroups(orgId),
            ]);
            const grants = [
                ...groups.map((g) => ({ type: 'group', id: g.id })),
                ...orgModules
                    .filter((om) => !om.groupId)
                    .map((om) => ({ type: 'module', id: om.id })),
            ];
            const bootstrapAudit = {
                source: 'system',
                reason: 'organization.bootstrapAdminProfile',
            };
            const admProfile = await profileService.createProfile(orgId, {
                name: 'Administrador',
                abbreviation: 'ADM',
                grants,
            }, { audit: bootstrapAudit });
            rtLogService.info('ADM profile created for organization', {
                orgId,
                profileId: admProfile.id,
                groupGrants: groups.length,
                looseModuleGrants: orgModules.filter((om) => !om.groupId).length,
            });
            // 5. Assign admin user to the "ADM" profile
            await profileService.assignUserToProfile(orgId, adminUserId, admProfile.id, {
                audit: bootstrapAudit,
            });
            rtLogService.info('Admin user assigned to ADM profile', { orgId, adminUserId, profileId: admProfile.id });
            return (await orgRepo.getById(orgId)) ?? newOrg;
        }
        catch (error) {
            rtLogService.error('Error creating organization', { orgData, adminUserId, error });
            throw error;
        }
    }
    /**
     * 0152 preferred path: write planIds, create base groups by tag from plan expansion,
     * then syncPlans (placement via autoTags). Skips action:'group' bases with warn.
     */
    async seedOrgFromDefaultPlans(orgId, orgRepo, defaultPlanIds) {
        await orgRepo.update(orgId, auditService.stampOnUpdate({ planIds: defaultPlanIds }));
        const [catalog, allPlans] = await Promise.all([
            moduleService.getAllModules(),
            planService.getAllPlans(),
        ]);
        const planIdSet = new Set(defaultPlanIds);
        const activePlans = allPlans.filter((p) => planIdSet.has(p.id) && lifecycleService.isActive(p));
        if (activePlans.length === 0) {
            rtLogService.warn('defaultPlanIds configured but no matching active plans', {
                orgId,
                defaultPlanIds,
            });
            return;
        }
        const { missing } = entitledModuleIds(activePlans, catalog, []);
        const catalogById = new Map(catalog.map((m) => [m.id, m]));
        const seedModules = [];
        for (const baseId of missing) {
            const base = catalogById.get(baseId);
            if (!base)
                continue;
            if (base.action === 'group') {
                rtLogService.warn('Skipping action:group base module while seeding new org from plans', { orgId, baseId, name: base.name });
                continue;
            }
            seedModules.push(base);
        }
        await this.createBaseGroupsByTags(orgId, seedModules);
        const syncResult = await organizationModuleService.syncPlans(orgId);
        rtLogService.info('Organization seeded from defaultPlanIds', {
            orgId,
            defaultPlanIds,
            created: syncResult.created.length,
        });
    }
    /** One ModuleGroup per distinct tag — name = tag, autoTags: [tag] (0149 §6 / 0152 §3.2). */
    async createBaseGroupsByTags(orgId, modules) {
        const tags = new Set();
        for (const m of modules) {
            for (const t of m.tags ?? []) {
                const n = normalizeTag(t);
                if (n)
                    tags.add(n);
            }
        }
        const tagToGroupId = new Map();
        for (const tag of [...tags].sort((a, b) => a.localeCompare(b))) {
            const group = await moduleGroupService.createGroup(orgId, {
                name: tag,
                autoTags: [tag],
            });
            tagToGroupId.set(tag, group.id);
        }
        if (tagToGroupId.size > 0) {
            rtLogService.info('Initial module groups created by tag', {
                orgId,
                groupCount: tagToGroupId.size,
            });
        }
        return tagToGroupId;
    }
}
export const organizationService = new OrganizationService();
//# sourceMappingURL=organizationService.js.map